import './ClubsPage.css';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import BottomSheet from '../../Components/BottomSheet/BottomSheet';
import MapPage from '../MapPage/MapPage';
import ClubDetailBottomSheet from '../../Components/ClubDetailBottomSheet/ClubDetailBottomSheet';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid, faComments } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllClubs,
  fetchLikedClubs,
  toggleLikeClub,
  fetchMatchPercentages,
} from '../../store/slices/clubsSlice';
import Navbar from '../../Components/Navbar/Navbar';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { timeManager } from '../../utils/timeUtils';

const ClubsPage = () => {
  const [selectedClub, setSelectedClub] = useState(null);
  const [isGenreSheetOpen, setIsGenreSheetOpen] = useState(false); // 장르 바텀시트
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false); // 정렬 바텀시트
  const [isClubDetailOpen, setIsClubDetailOpen] = useState(false); // 클럽 상세 바텀시트
  
  // 필터링 상태 추가
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedGenres, setSelectedGenres] = useState([]); // 배열로 변경
  const [sortOrder, setSortOrder] = useState('추천순'); // 거리순 -> 추천순으로 변경
  const [userLocation, setUserLocation] = useState(null); // 사용자 위치 추가

  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { allClubs, likedClubs, isLoading, matchPercentages } = useSelector((state) => state.clubs);

  const location = useLocation();
  const shouldPreserveState = location.state?.preserveState;

  // URL에서 playlist_id 확인
  const searchParams = new URLSearchParams(window.location.search);
  const playlistId = searchParams.get('playlist_id');

  // 이미지 뷰어 상태
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerImages, setViewerImages] = useState([]);

  const navigate = useNavigate();
  const authUser = useAuth();

  // 거리순 상태 선언
  const [distanceSortedClubs, setDistanceSortedClubs] = useState([]);

  // 클럽 좌표 캐시
  const clubCoordsCache = useRef(new Map());

  // 운영시간 파싱 및 당일 상태 계산 함수
  const getTodayOperatingStatus = (operatingHours) => {
    if (!operatingHours) {
      return { status: '정보 없음', time: '' };
    }

    // timeManager 확인
    if (typeof timeManager === 'undefined') {
      return { status: '정보 없음', time: '' };
    }

    // timeManager를 사용하여 압축 포맷과 기존 포맷 모두 지원
    const result = timeManager.getCurrentOperatingStatus(operatingHours);
    
    return result;
  };

  // 주소를 좌표로 변환하는 함수
  const geocodeAddress = useCallback((address) => {
    return new Promise((resolve) => {
      if (!address) {
        resolve(null);
        return;
      }
      if (!window.kakao || !window.kakao.maps) {
        resolve(null);
        return;
      }
      const geocoder = new window.kakao.maps.services.Geocoder();
      geocoder.addressSearch(address, (result, status) => {
        if (status === window.kakao.maps.services.Status.OK && result[0]) {
          const coords = {
            lat: parseFloat(result[0].y),
            lng: parseFloat(result[0].x)
          };
          resolve(coords);
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  // 클럽의 좌표를 가져오는 함수
  const getClubCoordinates = useCallback(async (club) => {
    if (!club.address) return null;
    
    const cacheKey = club.id.toString();
    if (clubCoordsCache.current.has(cacheKey)) {
      return clubCoordsCache.current.get(cacheKey);
    }

    const coords = await geocodeAddress(club.address);
    if (coords) {
      clubCoordsCache.current.set(cacheKey, coords);
    }
    return coords;
  }, [geocodeAddress]);

  // 거리 계산 함수 (Haversine 공식)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
    
    const R = 6371; // 지구의 반지름 (km)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // km 단위
    return distance;
  };

  // 위치 권한 요청 함수
  const requestLocationPermission = useCallback(async () => {
    if (!navigator.geolocation) {
      alert('이 브라우저는 위치 서비스를 지원하지 않습니다.');
      return false;
    }
    
    // HTTPS가 아닌 경우 경고
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      alert('위치 서비스는 HTTPS 환경에서만 작동합니다.');
      return false;
    }
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false, // 정확도를 낮춰서 더 빠르게 응답
          timeout: 30000, // 타임아웃을 30초로 늘림
          maximumAge: 300000 // 5분 이내의 캐시된 위치 정보 허용
        });
      });
      
      setUserLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      
      return true;
    } catch (error) {

      
      // 에러 코드별 메시지
      let errorMessage = '위치 권한을 가져올 수 없습니다.';
      switch (error.code) {
        case 1:
          errorMessage = '위치 권한이 거부되었습니다. 브라우저 설정에서 위치 권한을 허용해주세요.';
          break;
        case 2:
          errorMessage = '위치 정보를 사용할 수 없습니다.';
          break;
        case 3:
          errorMessage = '위치 정보 요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.';
          break;
      }
      
      alert(errorMessage);
      return false;
    }
  }, []);

  // clubsData 선언 (matchPercent 정렬 등)
  const clubsData = useMemo(() => {
    const baseData =
      matchPercentages && matchPercentages.length > 0
        ? matchPercentages
        : allClubs;
    
    const sorted = [...baseData].sort(
      (a, b) => (b.matchPercent ?? 0) - (a.matchPercent ?? 0)
    );
    
    return sorted;
  }, [matchPercentages, allClubs]);

  // processedClubs 선언 (운영시간, 이미지 등 가공)
  const processedClubs = useMemo(() => {
    return clubsData.map(club => {
      const operatingStatus = getTodayOperatingStatus(club.operating_hours);
      return {
        ...club,
        operatingStatus: operatingStatus.status,
        operatingTime: operatingStatus.time,
        images: club.images?.map(img => img.image_url) || [],
        genreList: club.genres ? club.genres.split(',').map(g => g.trim()) : []
      };
    });
  }, [clubsData]);

  // filteredClubs 선언 (검색/필터/정렬)
  const filteredClubs = useMemo(() => {
    let filtered = processedClubs.filter((club) => {
      const matchesSearch = club.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        (club.korean_name && club.korean_name.includes(searchQuery));
      const matchesLocation = selectedLocation
        ? club.location === selectedLocation
        : true;
      const matchesGenre = selectedGenres.length > 0 
        ? club.genres && selectedGenres.some(g => 
            club.genres.split(',').map((genre) => genre.trim().toUpperCase()).includes(g.toUpperCase())
          )
        : true;
      return matchesSearch && matchesLocation && matchesGenre;
    });
    if (sortOrder === '인기순') {
      filtered = filtered.sort((a, b) => (b.like_count ?? 0) - (a.like_count ?? 0));
    }
    return filtered;
  }, [processedClubs, searchQuery, selectedLocation, selectedGenres, sortOrder]);

  // 거리순 정렬 useEffect
  useEffect(() => {
    if (sortOrder === '거리순' && userLocation) {
      const calc = async () => {
        const clubsWithDistance = await Promise.all(
          filteredClubs.map(async (club) => {
            const coords = await getClubCoordinates(club);
            const distance = coords
              ? calculateDistance(userLocation.lat, userLocation.lng, coords.lat, coords.lng)
              : Infinity;
            
            return { ...club, distance };
          })
        );
        
        const sorted = clubsWithDistance.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        setDistanceSortedClubs(sorted);
      };
      calc();
    } else {
      setDistanceSortedClubs([]);
    }
  }, [sortOrder, userLocation, filteredClubs, getClubCoordinates]);

  // 최종 클럽 목록
  const finalFilteredClubs = sortOrder === '거리순' && distanceSortedClubs.length > 0
    ? distanceSortedClubs
    : filteredClubs;



  // 지역 목록 생성 (우선순위 지역을 앞에 배치)
  const locations = useMemo(() => {
    const allLocations = [...new Set(processedClubs.map((club) => club.location))];
    const priorityLocations = ['홍대', '이태원', '강남', '신사', '건대', '한남', '명동', '압구정'];
    
    // 우선순위 지역을 앞에, 나머지를 뒤에 배치
    const sortedLocations = [
      ...priorityLocations.filter(loc => allLocations.includes(loc)),
      ...allLocations.filter(loc => !priorityLocations.includes(loc))
    ];
    
    return sortedLocations;
  }, [processedClubs]);

  // 장르 목록 생성
  const genres = useMemo(() => {
    const allGenres = processedClubs.flatMap((club) =>
      club.genres ? club.genres.split(',').map((g) => g.trim()) : []
    );
    // HipHop, EDM, CLOSED 제외하고 대문자로 변환
    return [...new Set(allGenres)]
      .filter(genre => 
        !['HipHop', 'EDM', 'hiphop', 'edm', 'HIPHOP', 'CLOSED', 'closed', 'Closed'].includes(genre)
      )
      .map(genre => genre.toUpperCase()); // 대문자로 변환
  }, [processedClubs]);

  // 컴포넌트 마운트 시 클럽 데이터 가져오기
  useEffect(() => {
    if (shouldPreserveState) return;

    dispatch(fetchAllClubs())
      .unwrap()
      .then(() => {
        if (isAuthenticated && user) {
          dispatch(fetchLikedClubs(user.id));
        }
      });
  }, [dispatch, isAuthenticated, user, shouldPreserveState]);

  // matchPercentages 불러오기
  useEffect(() => {
    if (shouldPreserveState) return;
    

    
    // localStorage에서 이전 사용자의 매칭 퍼센트 데이터 복원
    const lastUserId = localStorage.getItem('lastUserId');
    const currentUserId = isAuthenticated && user?.id ? user.id : 'guest';
    
    // 다른 사용자가 로그인한 경우 localStorage 정리
    if (lastUserId && lastUserId !== currentUserId) {
      
      localStorage.removeItem('matchPercentages');
      localStorage.removeItem('lastPlaylistId');
      localStorage.removeItem('lastUserId');
      localStorage.removeItem('lastPlaylistUrl');
    }
    
    if (playlistId) {
      const finalUserId = isAuthenticated && user?.id ? user.id : 'guest';

      
      dispatch(fetchMatchPercentages({ userId: finalUserId, playlistId }))
        .unwrap()
        .then((result) => {
  
        })
        .catch((error) => {

        });
    } else {
      // URL에 playlist_id가 없으면 localStorage에서 복원 시도
      const savedPlaylistId = localStorage.getItem('lastPlaylistId');
      const savedUserId = localStorage.getItem('lastUserId');
      
      if (savedPlaylistId && savedUserId === currentUserId) {

        const finalUserId = isAuthenticated && user?.id ? user.id : 'guest';
        
        dispatch(fetchMatchPercentages({ userId: finalUserId, playlistId: savedPlaylistId }))
          .unwrap()
          .then((result) => {
  
          })
          .catch((error) => {

          });
      } else {

      }
    }
  }, [dispatch, playlistId, isAuthenticated, user, shouldPreserveState]);



  // 검색어 변경 핸들러
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // 정렬 순서 변경 핸들러 - 바텀시트로 변경
  const handleSortOrderClick = useCallback(() => {
    setIsSortSheetOpen(true);
  }, []);

  // 정렬 바텀시트 핸들러
  const handleCloseSortSheet = () => {
    setIsSortSheetOpen(false);
  };

  const handleSortSelect = async (sortType) => {
    if (sortType === '거리순') {
      const hasPermission = await requestLocationPermission();
      
      if (hasPermission) {
        setSortOrder('거리순');
        setIsSortSheetOpen(false); // 바텀시트 닫기
      } else {
        setSortOrder('인기순');
        setIsSortSheetOpen(false); // 바텀시트 닫기
        alert('위치 권한이 필요합니다. 인기순으로 정렬됩니다.');
      }
    } else {
      setSortOrder(sortType);
      setIsSortSheetOpen(false); // 바텀시트 닫기
    }
  };

  // 지역 필터 클릭 핸들러
  const handleLocationClick = useCallback((location) => {
    setSelectedLocation(prev => prev === location ? null : location);
  }, []);

  // 장르 필터 클릭 핸들러
  const handleGenreClick = useCallback((genre) => {
    setSelectedGenres(prev => {
      if (prev.includes(genre)) {
        return prev.filter(g => g !== genre);
      } else {
        return [...prev, genre];
      }
    });
  }, []);

  const handleLikeClick = (clubId) => {
    if (!user || !isAuthenticated) {
      alert('로그인이 필요합니다.');
      return;
    }
    
    const isCurrentlyLiked = likedClubs.includes(clubId);
    dispatch(toggleLikeClub({ userId: user.id, clubId, isLiked: isCurrentlyLiked }))
      .unwrap()
      .then(() => {
        dispatch(fetchAllClubs());

      })
      .catch((error) => {});
  };

  // 채팅 입장 핸들러 추가
  const handleJoinChat = (e, club) => {
    e.stopPropagation();
    if (!authUser) {
      alert('로그인 후 이용해주세요');
      return;
    }
    navigate(`/clubs/${club.id}/chat`, { state: { club } });
  };

  const handleCardClick = (club) => {
    setSelectedClub(club);
    setIsClubDetailOpen(true);
  };

  // 지도에서 클럽 선택 시 호출되는 함수
  const handleSelectClubFromMap = (club) => {
    setSelectedClub(club);
    setIsClubDetailOpen(true);
    
    // 바텀시트에서 해당 클럽 카드로 스크롤 이동
    const clubElement = document.getElementById(`club-${club.id}`);
    if (clubElement) {
      clubElement.scrollIntoView({ 
        behavior: 'smooth',     // 부드러운 스크롤
        block: 'center'         // 화면 중앙에 위치
      });
      
      // 하이라이트 효과 (2초간)
      clubElement.classList.add('highlight');
      setTimeout(() => {
        clubElement.classList.remove('highlight');
      }, 2000);
    }
  };

  // 장르 바텀시트 핸들러
  const handleOpenGenreSheet = () => {
    setIsGenreSheetOpen(true);
  };

  const handleCloseGenreSheet = () => {
    setIsGenreSheetOpen(false);
  };

  // 클럽 상세 바텀시트 핸들러
  const handleCloseClubDetail = () => {
    setIsClubDetailOpen(false);
  };

  // 이미지 클릭 핸들러
  const handleImageClick = (images, idx) => {
    setViewerImages(images);
    setViewerIndex(idx);
    setViewerOpen(true);
  };

  return (
    <div className="clubs-page">
      <Navbar />
      {/* 지도 영역 - 전체 화면 배경 */}
      <div className="clubs-page-map-container">
        <MapPage 
          clubs={finalFilteredClubs}
          selectedLocation={selectedLocation}
          isNearbyActive={false}
          onSelectClub={handleSelectClubFromMap}
          height="100vh"
        />
      </div>
      


      {/* 바텀시트 */}
      <BottomSheet
        isOpen={true}
        onClose={undefined}
        backgroundColor="#232323"
        snapPoints={[0.18, 0.55, 0.85]} // 15%(최소) -> 55%(기본) -> 85%(최대)
        initialSnap={1} // 55% 높이로 시작
        showDragHandle={true}
      >
        {/* 검색바와 필터를 바텀시트 내용 맨 위로 이동 */}
        <div className="bottom-sheet-search-header">
          <div className="search-bar-container">
            <input 
              type="text" 
              placeholder="클럽이름 검색" 
              className="search-input"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <div className="filter-tags">
            <span 
              className="filter-tag"
              onClick={handleSortOrderClick}
              style={{ cursor: 'pointer' }}
            >
              {sortOrder}
            </span>
            
          
            <span 
              className={`filter-tag ${selectedGenres.length > 0 ? 'active' : ''}`}
              onClick={handleOpenGenreSheet}
              style={{ cursor: 'pointer' }}
            >
              장르 {selectedGenres.length > 0 && `(${selectedGenres.length})`}
            </span>
            <span className="filter-divider">|</span>
            {/* 지역 필터 태그들 */}
            {locations.slice(0, 5).map((location) => (
              <span 
                key={location}
                className={`filter-tag ${selectedLocation === location ? 'active' : ''}`}
                onClick={() => handleLocationClick(location)}
                style={{ cursor: 'pointer' }}
              >
                {location}
              </span>
            ))}
          </div>
        </div>

        {/* 클럽 카드 목록 */}
        <div className="bottom-sheet-club-list">
          {isLoading ? (
            <div className="loading-spinner">
              <img
                src="/How2Play_mainLogo128.png"
                alt="Loading"
                className="logo-spin"
              />
            </div>
          ) : finalFilteredClubs.length === 0 ? (
            <p className="no-results">검색 결과가 없습니다.</p>
          ) : (
            finalFilteredClubs.map((club) => (
              <div key={club.id} className="bottom-sheet-club-container">
                <div 
                  className={`bottom-sheet-card ${selectedClub?.id === club.id ? 'selected' : ''}`}
                  onClick={() => handleCardClick(club)}
                  id={`club-${club.id}`}
                >
                  {/* 카드 헤더 */}
                  <div className="bottom-sheet-card-header">
                    <h3 className="bottom-sheet-card-title">{club.name}</h3>
                    <p className="bottom-sheet-card-category">{club.location}</p>
                    <p className="bottom-sheet-card-status">
                      {club.operatingStatus}
                      {club.operatingTime && club.operatingTime.trim() !== '' && ` · ${club.operatingTime}`}
                    </p>
                    <p className="bottom-sheet-card-genres">
                      {club.genreList.length > 0 ? club.genreList.join(' · ') : '장르 정보 없음'}
                    </p>
                  </div>

                  {/* 이미지 갤러리 */}
                  <div className="bottom-sheet-card-images">
                    {club.images && club.images.length > 0 ? (
                      club.images.map((image, index) => (
                        <div key={index} className="bottom-sheet-card-image">
                          <img
                            src={image}
                            alt={`${club.name} ${index + 1}`}
                            onClick={e => { e.stopPropagation(); handleImageClick(club.images, index); }}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="bottom-sheet-card-image">
                        <img src="/Images/default-club.jpg" alt="기본 이미지" />
                      </div>
                    )}
                  </div>

                  {/* 액션 버튼들 */}
                  <div className="bottom-sheet-card-actions">
                    {/* 채팅 버튼 */}
                    <button
                      className="bottom-sheet-card-chat"
                      onClick={(e) => handleJoinChat(e, club)}
                      title="채팅"
                    >
                      <FontAwesomeIcon icon={faComments} />
                    </button>
                    
                    {/* 북마크 버튼 */}
                    <button
                      className="bottom-sheet-card-bookmark"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikeClick(club.id);
                      }}
                      title="찜하기"
                    >
                      <FontAwesomeIcon
                        icon={likedClubs.includes(club.id) ? faHeartSolid : faHeartRegular}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </BottomSheet>

      {/* 장르 선택 바텀시트 */}
      <BottomSheet
        isOpen={isGenreSheetOpen}
        onClose={handleCloseGenreSheet}
        backgroundColor="#232323"
        snapPoints={[0.55]} // 50% 한 단계
        initialSnap={0}
        showDragHandle={true}
      >
        <div className="genre-sheet-content">
          <h2 className="genre-sheet-title">장르 선택</h2>
          <div className="genre-options">
            {genres.map((genre) => (
              <button
                key={genre}
                className={`genre-option ${selectedGenres.includes(genre) ? 'selected' : ''}`}
                onClick={() => handleGenreClick(genre)}
              >
                {genre}
              </button>
            ))}
          </div>
          <div className="genre-actions">
            {selectedGenres.length > 0 && (
              <button 
                className="genre-clear-btn"
                onClick={() => {
                  setSelectedGenres([]);
                }}
              >
                전체 해제
              </button>
            )}
            <button 
              className="genre-apply-btn"
              onClick={handleCloseGenreSheet}
            >
              적용하기
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* 정렬 바텀시트 */}
      <BottomSheet
        isOpen={isSortSheetOpen}
        onClose={handleCloseSortSheet}
        backgroundColor="#232323"
        snapPoints={[0.55]} // 50% 한 단계
        initialSnap={0}
        showDragHandle={true}
      >
        <div className="sort-sheet-content">
          <h2 className="sort-sheet-title">정렬 선택</h2>
          <div className="sort-options">
            <button
              className={`sort-option ${sortOrder === '추천순' ? 'selected' : ''}`}
              onClick={() => handleSortSelect('추천순')}
            >
              추천순
            </button>
            <button
              className={`sort-option ${sortOrder === '인기순' ? 'selected' : ''}`}
              onClick={() => handleSortSelect('인기순')}
            >
              인기순
            </button>
            <button
              className={`sort-option ${sortOrder === '거리순' ? 'selected' : ''}`}
              onClick={() => handleSortSelect('거리순')}
            >
              거리순
            </button>
          </div>
          <div className="sort-actions">
            <button 
              className="sort-apply-btn"
              onClick={handleCloseSortSheet}
            >
              적용하기
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* 클럽 상세 바텀시트 */}
      <ClubDetailBottomSheet
        isOpen={isClubDetailOpen}
        onClose={handleCloseClubDetail}
        club={selectedClub}
      />

      {/* 풀스크린 이미지 뷰어 */}
      {viewerOpen && (
        <Lightbox
          mainSrc={viewerImages[viewerIndex]}
          nextSrc={viewerImages[(viewerIndex + 1) % viewerImages.length]}
          prevSrc={viewerImages[(viewerIndex + viewerImages.length - 1) % viewerImages.length]}
          onCloseRequest={() => setViewerOpen(false)}
          onMovePrevRequest={() =>
            setViewerIndex((viewerIndex + viewerImages.length - 1) % viewerImages.length)
          }
          onMoveNextRequest={() =>
            setViewerIndex((viewerIndex + 1) % viewerImages.length)
          }
          imagePadding={0}
          enableZoom={false}
          clickOutsideToClose={true}
          animationDuration={300}
        />
      )}
    </div>
  );
};

export default ClubsPage;